import { useId, type ReactNode } from 'react'
import { LEARNER_EMAIL_SUFFIX } from '@/utils/learnerEmail'

interface LearnerHandleInputProps {
  value: string
  onChange: (next: string) => void
  id?: string
  name?: string
  required?: boolean
  disabled?: boolean
  leftIcon?: ReactNode
  /** 검증 에러 메시지(빨강 톤 + 하단 메시지). */
  error?: string
  /** amber 강조(예: 이메일 변경 경고). error가 우선. */
  warning?: boolean
  placeholder?: string
  autoFocus?: boolean
  /** 'lc' = 로그인용 LC 토큰 셸, 'plain' = admin 폼 셸(기본). */
  variant?: 'plain' | 'lc'
}

export function LearnerHandleInput({
  value,
  onChange,
  id,
  name,
  required,
  disabled,
  leftIcon,
  error,
  warning,
  placeholder = 'abc1234',
  autoFocus,
  variant = 'plain',
}: LearnerHandleInputProps) {
  const reactId = useId()
  const baseId = id ?? reactId
  const suffixId = `${baseId}-suffix`
  const errorId = `${baseId}-error`

  const commonInputProps = {
    id: baseId,
    name,
    type: 'text' as const,
    required,
    disabled,
    autoFocus,
    autoComplete: 'username',
    autoCapitalize: 'off',
    spellCheck: false,
    value,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    'aria-describedby': error ? errorId : suffixId,
    'aria-invalid': !!error,
    placeholder,
  }

  if (variant === 'lc') {
    const border = error
      ? 'border-lc-error-500 focus-within:ring-lc-error-100'
      : warning
        ? 'border-lc-warning-500 focus-within:ring-lc-warning-100'
        : 'border-lc-disabled-border focus-within:border-lc-key-500 focus-within:ring-lc-key-100'
    return (
      <div>
        <div className={`flex h-12 items-center gap-3 rounded-xl border bg-lc-v3-gray-50 px-4 transition-[border-color,box-shadow] focus-within:ring-2 ${border}`}>
          {leftIcon}
          <input
            {...commonInputProps}
            className="min-w-0 flex-1 bg-transparent text-lc-v3-body-2 font-medium text-lc-text-primary outline-none placeholder:text-lc-text-disabled"
          />
          <span id={suffixId} className="shrink-0 select-none text-lc-v3-body-2 text-lc-text-tertiary">
            {LEARNER_EMAIL_SUFFIX}
          </span>
        </div>
        {error && <p id={errorId} className="mt-1.5 text-lc-v3-caption-1 font-semibold text-lc-text-error">{error}</p>}
      </div>
    )
  }

  const border = error
    ? 'border-red-400 focus-within:ring-red-500'
    : warning
      ? 'border-amber-400 focus-within:ring-amber-500'
      : 'border-gray-300 focus-within:ring-primary-500'
  return (
    <div>
      <div className={`flex items-center w-full px-4 py-2 border rounded-lg focus-within:ring-2 ${border}`}>
        {leftIcon}
        <input
          {...commonInputProps}
          className="min-w-0 flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder-gray-400"
        />
        <span id={suffixId} className="ml-1 text-sm text-gray-400 select-none shrink-0">
          {LEARNER_EMAIL_SUFFIX}
        </span>
      </div>
      {error && <p id={errorId} className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}
